/**
 * In-app user notifications (header bell).
 * Types: memory_invite, friend_accepted, event_day, giveaway_win, giveaway_instructions
 * (incoming requests stay on friendRequests; chat rows stay on the inbox).
 */

import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { getUserProfile } from './userProfile';

export const USER_NOTIFICATIONS_COLLECTION = 'userNotifications';
export const NOTIF_TYPE_MEMORY_INVITE = 'memory_invite';
export const NOTIF_TYPE_FRIEND_ACCEPTED = 'friend_accepted';
export const NOTIF_TYPE_EVENT_DAY = 'event_day';
export const NOTIF_TYPE_GIVEAWAY_WIN = 'giveaway_win';
export const NOTIF_TYPE_GIVEAWAY_INSTRUCTIONS = 'giveaway_instructions';

export function memoryInviteNotifId(memoryId, toUid) {
  return `memory_${memoryId}_${toUid}`;
}

export function friendAcceptedNotifId(requestId) {
  return `friend_accepted_${requestId}`;
}

export function eventDayNotifId(eventId, uid) {
  const safeEventId = String(eventId || '').replace(/[~*/.[\]]/g, '_');
  return `event_day_${safeEventId}_${uid}`;
}

export function normalizeUserNotification(id, data = {}) {
  return {
    id,
    type: data.type || '',
    toUid: data.toUid || '',
    fromUid: data.fromUid || '',
    memoryId: data.memoryId || null,
    memoryTitle: typeof data.memoryTitle === 'string' ? data.memoryTitle : '',
    status: data.status === 'read' ? 'read' : 'unread',
    createdAt: data.createdAt || null,
    eventId: data.eventId || '',
    eventTitle: typeof data.eventTitle === 'string' ? data.eventTitle : '',
    location: typeof data.location === 'string' ? data.location : '',
    eventDayKey: data.eventDayKey || '',
    body: typeof data.body === 'string' ? data.body : '',
  };
}

export function isBellUserNotification(n) {
  return (
    n?.status === 'unread' &&
    (n.type === NOTIF_TYPE_MEMORY_INVITE
      || n.type === NOTIF_TYPE_FRIEND_ACCEPTED
      || n.type === NOTIF_TYPE_EVENT_DAY
      || n.type === NOTIF_TYPE_GIVEAWAY_WIN
      || n.type === NOTIF_TYPE_GIVEAWAY_INSTRUCTIONS)
  );
}

/** Bell history: invites, accepted requests, event-day reminders, and giveaway notes. Not chat. */
export function isHistoryUserNotification(n) {
  return (
    n?.type === NOTIF_TYPE_MEMORY_INVITE
    || n?.type === NOTIF_TYPE_FRIEND_ACCEPTED
    || n?.type === NOTIF_TYPE_EVENT_DAY
    || n?.type === NOTIF_TYPE_GIVEAWAY_WIN
    || n?.type === NOTIF_TYPE_GIVEAWAY_INSTRUCTIONS
  );
}

/**
 * Create unread memory-invite notifs for invitees (idempotent doc ids).
 */
export async function createMemoryInviteNotifications({
  fromUid,
  memoryId,
  memoryTitle = '',
  toUids = [],
}) {
  if (!fromUid || !memoryId) return [];
  const targets = [...new Set((toUids || []).filter((id) => id && id !== fromUid))];
  if (!targets.length) return [];
  const batch = writeBatch(db);
  const title = String(memoryTitle || '').trim();
  targets.forEach((toUid) => {
    const id = memoryInviteNotifId(memoryId, toUid);
    batch.set(
      doc(db, USER_NOTIFICATIONS_COLLECTION, id),
      {
        type: NOTIF_TYPE_MEMORY_INVITE,
        toUid,
        fromUid,
        memoryId,
        memoryTitle: title,
        status: 'unread',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  });
  await batch.commit();
  return targets.map((toUid) => memoryInviteNotifId(memoryId, toUid));
}

export async function markUserNotificationRead(notificationId, uid) {
  if (!notificationId || !uid) throw new Error('Missing notification or user.');
  await updateDoc(doc(db, USER_NOTIFICATIONS_COLLECTION, notificationId), {
    status: 'read',
    updatedAt: serverTimestamp(),
  });
}

export async function labelUserNotifications(rows = []) {
  const list = rows || [];
  const profiles = await Promise.all(
    list.map((n) => (
      n.type === NOTIF_TYPE_EVENT_DAY
      || n.type === NOTIF_TYPE_GIVEAWAY_WIN
      || n.type === NOTIF_TYPE_GIVEAWAY_INSTRUCTIONS
        ? null
        : getUserProfile(n.fromUid)
    ))
  );
  return list.map((n, i) => {
    if (n.type === NOTIF_TYPE_EVENT_DAY) {
      return {
        ...n,
        kind: 'event_day',
        fromLabel: '',
        fromPhotoURL: null,
        fromUsername: null,
      };
    }
    if (n.type === NOTIF_TYPE_GIVEAWAY_WIN || n.type === NOTIF_TYPE_GIVEAWAY_INSTRUCTIONS) {
      return {
        ...n,
        kind: n.type,
        fromLabel: '',
        fromPhotoURL: null,
        fromUsername: null,
      };
    }
    const p = profiles[i];
    const kind =
      n.type === NOTIF_TYPE_FRIEND_ACCEPTED ? 'friend_accepted' : 'memory_invite';
    return {
      ...n,
      kind,
      fromLabel:
        p?.displayName ||
        (p?.username ? `@${p.username}` : '') ||
        p?.email ||
        n.fromUid,
      fromPhotoURL: p?.photoURL || null,
      fromUsername: p?.username || null,
    };
  });
}

/** Live bell notifications for `uid`, including read history. Chat rows stay on the inbox. */
export function subscribeUnreadUserNotifications(uid, onChange, onError) {
  if (!uid) {
    onChange([]);
    return () => {};
  }
  const q = query(
    collection(db, USER_NOTIFICATIONS_COLLECTION),
    where('toUid', '==', uid)
  );
  return onSnapshot(
    q,
    async (snap) => {
      try {
        const rows = snap.docs
          .map((d) => normalizeUserNotification(d.id, d.data()))
          .filter(isHistoryUserNotification);
        const labeled = await labelUserNotifications(rows);
        onChange(labeled);
      } catch (err) {
        if (onError) onError(err);
        else console.error('label user notifications failed:', err);
      }
    },
    (err) => {
      if (onError) onError(err);
      else console.error('subscribeUnreadUserNotifications failed:', err);
    }
  );
}
